import { useCallback } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { ZBError } from '@zenbusiness/application-commons-logging';

import { useDashboardPath } from './useDashboardPath';

/**
 * All builders default with required fields as ['businessEntityUuid'].
 */
const URL_CONFIG = {
  annual_report: {
    target: 'dashboard',
    basePath: '/compliance/annual-reports'
  },
  basic_email: {
    target: 'dashboard',
    basePath: '/marketing-tools/emails'
  },
  beneficial_ownership_filing: {
    target: 'dashboard',
    basePath: '/f/go/complete_beneficial_ownership_filing'
  },
  business_credit_cards: {
    target: 'external',
    basePath: 'https://creditcards.chase.com/a1/DAO/Ink-Homepage/Cash1124',
    options: {
      CELL: '6H8X',
      AFFID: 'A_HiQlD3Yhw-mCLb_axDjz0K9L24JhoJPg',
      idAliases: ['pvid'],
      jp_cmp: 'cc/1754462/aff/3-10004686/na'

    }
  },
  business_licenses: {
    target: 'dashboard',
    basePath: '/compliance/business-licenses'
  },
  business_license_report: {
    target: 'dashboard',
    basePath: '/compliance/business-licenses'
  },
  business_license_filing: {
    target: 'dashboard',
    basePath: '/compliance/business-licenses/filing-details'
  },
  change_of_registered_agent: {
    target: 'external',
    basePath: '/r/register-cora/init',
    options: {
      idAliases: ['companyId'],
      fromLinkModal: true,
      fromDashboard: true
    }
  },
  constant_contact: {
    target: 'external',
    basePath: 'https://constant-contact.ibfwsl.net/c/3633958/905514/3411',
    options: {
      idAliases: ['subId1', 'sharedid'],
      subId2: '30DayJourneyEmail'
    }
  },
  corp_amendment: {
    target: 'external',
    basePath: '/r/amendment/init',
    options: {
      idAliases: ['business_entity_uuid'],
      fromDashboard: true
    }
  },
  domain_name_reg: {
    target: 'dashboard',
    basePath: '/marketing-tools/domains'
  },
  legal_shield: {
    target: 'external',
    basePath: 'https://legalshield.sjv.io/kOzGvd'
  },
  llc_amendment: {
    target: 'external',
    basePath: '/r/amendment/init',
    options: {
      idAliases: ['business_entity_uuid'],
      fromDashboard: true
    }
  },
  logo_kit: {
    target: 'external',
    basePath: '/lb/a/wizard'
  },
  microsoft: {
    target: 'external',
    basePath: 'https://www.microsoft.com/en-us/microsoft-365/business/zenbusiness-partner-offer?ocid=cmmdpc9xzvo?zbid=%5BUUID%5D',
    searchParamDelimiter: '?',
    options: {
      ocid: 'cmmtyxdgaec',
      idAliases: ['zbid']
    }
  },
  next_insurance: {
    target: 'external',
    basePath: 'https://nextinsurance.sjv.io/ZQmD1z',
    options: {
      irclickid: 'TGdTA9QZtxyKUG53iwSRIwmAUks1-TzUv3zq1k0',
      channel: 'affiliate_direct',
      utm_source: 'impact',
      utm_campaign: 'ZenBusiness',
      adid: '1148969',
      campaignid: '14516',
      irgwc: '1',
      serial: '992855780',
      prev_tracking_id: 'df967d774689fc46079c5a4c476509b2',
      tracking_id: '26a417f2935656c49d147f644e8a9f87'
    }
  },
  ocoya: {
    target: 'external',
    basePath: 'https://www.ocoya.com/?via=zenbusiness'
  },
  registered_agent: {
    target: 'external',
    basePath: '/r/register-cora/init',
    options: {
      idAliases: ['companyId'],
      fromLinkModal: true,
      fromDashboard: true
    }
  },
  zb_tax_advice_biztax_prep: {
    target: 'external',
    basePath: '/r/tax-services/init',
    options: {
      idAliases: ['companyId'],
      fromDashboard: true,
      product: 'zb_tax_advice_biztax_prep'
    }
  },
  zb_tax_advice: {
    target: 'external',
    basePath: '/r/tax-services/init',
    options: {
      idAliases: ['companyId'],
      fromDashboard: true,
      product: 'zb_tax_advice'
    }
  },
  zb_money_pro_month: {
    target: 'external',
    basePath: '/r/register-zbmpro/init',
    options: {
      idAliases: ['companyId'],
      isMonthly: true,
      fromDashboard: true
    }
  },
  zb_money_pro_year: {
    target: 'external',
    basePath: '/r/register-zbmpro/init',
    options: {
      idAliases: ['companyId'],
      isMonthly: false,
      fromDashboard: true
    }
  },
  website_tools_bundle: {
    target: 'external',
    basePath: '/r/onlinetools-registration/init',
    options: {
      idAliases: ['companyId'],
      fromDashboard: true
    }
  },
  wfg_initial_report: {
    target: 'external',
    basePath: '/r/register-wfgir/init',
    options: {
      idAliases: ['companyId'],
      fromDashboard: true
    }
  },
  corp_formation: {
    target: 'external',
    basePath: '/shop/corp',
    options: {
      entryUri: '/dashboard'
    }
  },
  llc_formation: {
    target: 'external',
    basePath: '/formation-types-compare'
  },
  s_corp_designator: {
    target: 'external',
    basePath: '/shop/s-corp'
  },
  static_website: {
    target: 'dashboard',
    basePath: '/marketing-tools/websites'
  },
  bank_of_america_offer: {
    target: 'dashboard',
    basePath: '/finances?boa_modal=1'
  }
};

export default function useCartUrl() {
  const { businessEntityUuid } = useBusinessEntity();
  const dashboardPath = useDashboardPath();

  return useCallback(
    (product) => {
      const {
        target, basePath, searchParamDelimiter, options = {}
      } = URL_CONFIG[product] || {};

      if (!target) {
        throw new ZBError(`Could not find cart url for ${product}`);
      }

      if (target === 'external') {
        const { idAliases, ...data } = options;
        const url = new URL(basePath, window.location);
        const searchParams = new URLSearchParams();

        Object.entries(data).forEach(([key, value]) => {
          searchParams.set(key, value);
          url.search = searchParams.toString();
        });

        if (Array.isArray(idAliases)) {
          idAliases.forEach((alias) => {
            searchParams.set(alias, businessEntityUuid);
          });
          url.search = searchParams.toString();
        }

        if (searchParamDelimiter) {
          url.search = url.search.replace(/&/g, searchParamDelimiter);
        }

        return decodeURIComponent(String(url));
      }

      return `${dashboardPath}${basePath}`;
    },
    [businessEntityUuid, dashboardPath]
  );
}
